import { HttpAction } from "@megaron/http-service";
import { Serializers, SerializerValue } from "@megaron/serializers";
import { uuidSerializer } from "@megaron/uuid";

export const priceCurrentStatuses = ["active", "expired", "upcoming"] as const;
export type PriceCurrentStatus = (typeof priceCurrentStatuses)[number];

export const priceSortFields = [
  "priceList",
  "itemId",
  "itemName",
  "gtin",
  "value",
  "activeFrom",
  "activeUntilExclusive",
  "modifiedAt",
] as const;
export type PriceSortField = (typeof priceSortFields)[number];

export const priceListSerializer = Serializers.object({
  name: Serializers.string,
  currency: Serializers.string.nullable({ emptyStrAsNull: true }),
});

export type PriceList = SerializerValue<typeof priceListSerializer>;

export const priceSerializer = Serializers.object({
  id: uuidSerializer,
  itemId: Serializers.string,
  priceList: Serializers.string,
  value: Serializers.decimal.nullable({ emptyStrAsNull: true }),
  comment: Serializers.string.nullable({ emptyStrAsNull: true }),
  activeFrom: Serializers.dateOnly,
  createdBy: Serializers.string,
  createdAt: Serializers.datetime,
  modifiedBy: Serializers.string,
  modifiedAt: Serializers.datetime,
});

export type Price = SerializerValue<typeof priceSerializer>;

export const priceSheetRowSerializer = Serializers.object({
  priceList: Serializers.string,
  itemId: Serializers.string.nullable({ emptyStrAsNull: true }),
  itemName: Serializers.string.nullable({ emptyStrAsNull: true }),
  value: Serializers.decimal.nullable({ emptyStrAsNull: true }),
  comment: Serializers.string.nullable({ emptyStrAsNull: true }),
  activeFrom: Serializers.dateOnly,
});

export type PriceSheetRow = SerializerValue<typeof priceSheetRowSerializer>;

export const shopifyPriceSerializer = Serializers.object({
  itemId: Serializers.string,
  itemName: Serializers.string.nullable({ emptyStrAsNull: true }),
  price: Serializers.decimal,
});

export type ShopifyPrice = SerializerValue<typeof shopifyPriceSerializer>;

export const legacyMegaraxPriceSerializer = Serializers.object({
  priceList: Serializers.string,
  itemId: Serializers.string.nullable({ emptyStrAsNull: true }),
  itemName: Serializers.string.nullable({ emptyStrAsNull: true }),
  gtin: Serializers.string.nullable({ emptyStrAsNull: true }),
  value: Serializers.decimal.nullable({ emptyStrAsNull: true }),
  comment: Serializers.string.nullable({ emptyStrAsNull: true }),
  activeFrom: Serializers.dateOnly,
  createdBy: Serializers.string.nullable({ emptyStrAsNull: true }),
  createdAt: Serializers.datetime,
  modifiedBy: Serializers.string.nullable({ emptyStrAsNull: true }),
  modifiedAt: Serializers.datetime,
});

export type LegacyMegaraxPrice = SerializerValue<typeof legacyMegaraxPriceSerializer>;

export const activePriceSerializer = Serializers.object({
  id: uuidSerializer,
  itemId: Serializers.string,
  itemName: Serializers.string.nullable(),
  gtin: Serializers.string.nullable(),
  priceList: Serializers.string,
  currency: Serializers.string.nullable(),
  value: Serializers.decimal,
  comment: Serializers.string.nullable(),
  currentStatus: Serializers.stringOneOf(...priceCurrentStatuses),
  activeFrom: Serializers.dateOnly,
  activeUntilExclusive: Serializers.dateOnly.nullable(),
  createdBy: Serializers.string,
  createdAt: Serializers.datetime,
  modifiedBy: Serializers.string,
  modifiedAt: Serializers.datetime,
});

export type ActivePrice = SerializerValue<typeof activePriceSerializer>;

export const priceListActions = {
  priceListsQuery: HttpAction({
    path: "/priceLists",
    method: "get",
    requiresAuth: true as const,
    valueSerializer: Serializers.object({
      priceLists: priceListSerializer.array(),
      count: Serializers.integer,
    }),
  }),

  pricesQuery: HttpAction({
    path: "/prices",
    method: "get",
    qsSerializer: Serializers.object({
      priceList: Serializers.string,
      priceLists: Serializers.string.array().asJsonString(),
      item: Serializers.string,
      items: Serializers.string.array().asJsonString(),
      searchText: Serializers.string,
      currentStatus: Serializers.stringOneOf(...priceCurrentStatuses).array().asJsonString(),
      activeOn: Serializers.dateOnly,
      sort: Serializers.sortFilter(priceSortFields).asJsonString(),
    }).partial(),
    requiresAuth: true as const,
    valueSerializer: Serializers.object({
      prices: activePriceSerializer.array(),
      count: Serializers.integer,
    }),
  }),
};
